import { useCallback, useContext, useMemo, useState } from 'react';
import type * as Ex from 'remirror/extensions';
import type { PlaceholderOptions } from 'remirror/extensions';
import {
  BoldExtension,
  DocExtension,
  ItalicExtension,
  ParagraphExtension,
  PlaceholderExtension,
  TextExtension,
  UnderlineExtension,
} from 'remirror/extensions';
import type { InvalidContentHandler, RemirrorEventListener } from '@remirror/core';
import { EditorComponent, Remirror as RemirrorReact, useRemirror } from '@remirror/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Remirror } from '@trinity/chat';
import { queryKeys } from '@trinity/chat-components/query-keys/index.js';
import { ButtonSet } from '../components/Modal/ButtonSet';
import { Header } from '../components/Modal/Header';
import { Modal } from '../components/Modal/Modal';
import { Button } from '../components/presentation/Button';
import { Toolbar } from '../components/Remirror/Toolbar';
import { BoldButton, ItalicButton, UnderlineButton } from '../components/Remirror/Toolbar.Commands';
import { useChatApiClient } from '../containers/Chat/useApiClient';
import { QuantAnalysisContext } from '../containers/QuantAnalysis/context';
import styles from './style/useUpdateQuantAnalysisDescriptionModal.module.css';

const DESCRIPTION_PLACEHOLDER =
`Client Name: Product 1, Product 2, ...

Current Competitors: Competitor A (Referred in Research as X), ...
Future Competitors: Competitor B (Referred in Research as Y), ..
`;

export const defaultDescriptionContent: Remirror.RootNode = {
  type: Remirror.NodeType.Doc,
  content: [{
    type: Remirror.NodeType.Paragraph,
    content: [],
  }],
};

type DescriptionEditorExtension =
  | Ex.PlaceholderExtension
  | Ex.TextExtension
  | Ex.ParagraphExtension
  | Ex.DocExtension
  | Ex.BoldExtension
  | Ex.ItalicExtension
  | Ex.UnderlineExtension;

function useDescriptionEditor({ initialState, placeholder }: { initialState?: Remirror.RootNode; placeholder?: string }) {
  const onError: InvalidContentHandler = useCallback(({ json, invalidContent, transformers }) => {
    return transformers.remove(json, invalidContent);
  }, []);

  const placeholderExtensionParams: PlaceholderOptions = {
    placeholder,
  };

  return useRemirror({
    content: initialState || defaultDescriptionContent,
    extensions: () => [
      new DocExtension({}),
      new PlaceholderExtension(placeholderExtensionParams),
      new ParagraphExtension({}),
      new TextExtension(),
      new BoldExtension({}),
      new ItalicExtension(),
      new UnderlineExtension(),
    ],
    onError,
  });
}

function isDescriptionEmpty(value: Remirror.RootNode | null | undefined) {
  if (!value) return true;

  function isParagraphNodeEmpty(node: Remirror.ParagraphNode) {
    const content = node.content || [];
    if (!content.length) return true;

    return content.every(isParagraphContentEmpty);
  }

  function isParagraphContentEmpty(node: Remirror.TextNode) {
    if (node.type === Remirror.NodeType.Text) {
      return !node.text.trim().length;
    }

    return false;
  }

  return (value.content || []).reduce((acc, node) => {
    if (!acc) return acc;
    return isParagraphNodeEmpty(node);
  }, true);
}

type UseUpdateQuantAnalysisDescriptionModalProps = {
  projectId: number | undefined;
  quantAnalysisId: number;
};

export const useUpdateQuantAnalysisDescriptionModal = ({
  projectId,
  quantAnalysisId,
}: UseUpdateQuantAnalysisDescriptionModalProps) => {
  const quantAnalysis = useContext(QuantAnalysisContext);
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState<Remirror.RootNode>(
    quantAnalysis?.description || defaultDescriptionContent
  );

  const { manager, state } = useDescriptionEditor({
    initialState: description || defaultDescriptionContent,
    placeholder: DESCRIPTION_PLACEHOLDER,
  });

  const apiClient = useChatApiClient();

  const toggleModal = useCallback(() => setIsOpen((open) => !open), []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const mutation = useMutation({
    mutationFn: () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      return apiClient.quantAnalysis.updateProjectQuantAnalysisDescription({
        projectId,
        quantAnalysisId,
        description,
      });
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: queryKeys.projects.quantAnalysis.items({ projectId, quantAnalysisId }),
        })
        .catch(() => {});

      setIsOpen(false);
    },
  });

  const canSubmit = useMemo(() => {
    return !isDescriptionEmpty(description);
  }, [description]);

  const handleDescriptionChange: RemirrorEventListener<DescriptionEditorExtension> = useCallback(
    (params) => {
      if (!params.tr?.docChanged) return;

      const value = params.state.doc.toJSON() as Remirror.RootNode;

      setDescription(value);
    },
    []
  );

  const UpdateDescriptionModalComponent = (
    <Modal
      open={isOpen}
      onClose={handleClose}>
      <Header text="Edit Analysis Description" />

      <div className={styles.body}>
        <div className={styles.description}>
          <div className={styles.label}>
            {`We highly recommend adding at least one sentence on who the product's client is, and a description of the current and future state competitors, if relevant (including how they were referred to in the research).`}
          </div>
          <RemirrorReact
            autoFocus
            manager={manager}
            onChange={handleDescriptionChange}
            initialContent={state}>
            <Toolbar className={styles.toolbar}>
              <BoldButton />
              <ItalicButton />
              <UnderlineButton />
            </Toolbar>
            <div className={styles.editor}>
              <EditorComponent />
            </div>
          </RemirrorReact>
        </div>
      </div>

      <ButtonSet>
        <Button.Destructive
          onClick={handleClose}
          disabled={mutation.isPending}>
          Cancel
        </Button.Destructive>

        <Button.Primary
          onClick={() => mutation.mutate()}
          disabled={!canSubmit || mutation.isPending}>
          {mutation.isPending ? 'Saving...' : 'Confirm'}
        </Button.Primary>
      </ButtonSet>
    </Modal>
  );

  return [toggleModal, UpdateDescriptionModalComponent] as const;
};
